import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import PageMeta from '../../../components/base/PageMeta';
import JsonLd from '../../../components/base/JsonLd';
import FloatingCtaBar from '../../../components/feature/FloatingCtaBar';
import SiteFooter from '../../../components/feature/SiteFooter';

const tools = [
  {
    name: 'InVideo',
    slug: 'invideo',
    tagline: 'AI-Powered Video Creation Platform',
    desc: 'InVideo is a comprehensive AI video creation platform that allows anyone to create professional-quality videos in minutes. With 5,000+ templates, AI script writing, text-to-video, and smart editing tools.',
    tags: ['Video Creation', 'AI Editing', 'Templates', 'Marketing'],
    rating: 4.7,
    reviews: 2840,
    price: 'Free · From $25/mo',
    priceType: 'free',
    link: 'https://invideo.sjv.io/75RQoQ',
    logoIcon: 'ri-video-line',
    logoGrad: 'from-orange-500 to-red-500',
    logoUrl: 'https://storage.readdy-site.link/project_files/3785ac0c-31ac-4f9e-b2ab-27ad3777db2a/d31ba726-bb64-4338-bac7-f2c2f30c60d1_logo-invideo.webp?v=fbac35169c97b48dfecbd56be24ff2d6',
    badge: 'Top Pick',
    badgeColor: 'bg-amber-100 text-amber-700',
  },
  {
    name: 'Skylum',
    slug: 'skylum',
    tagline: 'AI Photo Editing Made Effortless',
    desc: 'Skylum\'s Luminar Neo is an AI-powered photo editing software featuring Sky AI, Portrait AI, Relight AI, and Background Removal. Helps photographers achieve stunning professional results.',
    tags: ['Photo Editing', 'AI Retouching', 'Sky Replacement', 'Photography'],
    rating: 4.6,
    reviews: 1890,
    price: 'From $9.95/mo',
    priceType: 'paid',
    link: 'https://skylum.evyy.net/6ky4PG',
    logoIcon: 'ri-image-edit-line',
    logoGrad: 'from-sky-500 to-cyan-600',
    logoUrl: 'https://storage.readdy-site.link/project_files/3785ac0c-31ac-4f9e-b2ab-27ad3777db2a/aa3231b3-00cf-4b42-9172-0be3edafc70d_skylum.png?v=bc514d19c135e6c0750a5078de7b030e',
    badge: '',
    badgeColor: '',
  },
  {
    name: 'HeadshotPro',
    slug: 'headshotpro',
    tagline: 'AI Professional Headshots in Minutes',
    desc: 'HeadshotPro generates 120+ studio-quality AI headshots from your selfies in under 2 hours. Perfect for LinkedIn, corporate bios, and professional profiles — at a fraction of studio photography cost.',
    tags: ['AI Headshots', 'Professional Photos', 'Portrait AI', 'Photography'],
    rating: 4.8,
    reviews: 2640,
    price: 'From $29 (one-time)',
    priceType: 'paid',
    link: 'https://www.headshotpro.com/?via=hao',
    logoIcon: 'ri-camera-3-line',
    logoGrad: 'from-rose-500 to-pink-600',
    logoUrl: 'https://www.headshotpro.com/_nuxt/img/logo.0ed250a.svg',
    badge: 'Best Value',
    badgeColor: 'bg-rose-100 text-rose-700',
  },
  {
    name: 'Magai',
    slug: 'magai',
    tagline: 'Your All-in-One AI Chat Hub',
    desc: 'Magai is a unified AI chat interface that brings together GPT-4, Claude, Gemini, and more into one seamless platform. Create custom AI personas, save conversations, and switch between models easily.',
    tags: ['AI Chat', 'GPT-4', 'Claude', 'Productivity'],
    rating: 4.5,
    reviews: 980,
    price: 'From $9/mo',
    priceType: 'paid',
    link: 'https://magai.co?via=nhu',
    logoIcon: 'ri-message-3-line',
    logoGrad: 'from-indigo-500 to-violet-600',
    logoUrl: 'https://storage.readdy-site.link/project_files/3785ac0c-31ac-4f9e-b2ab-27ad3777db2a/960c73ec-c1ea-4630-a21c-74c5e7f0119c_magai.webp?v=953c8cff9338bc8777085a3d19560618',
    badge: '',
    badgeColor: '',
  },
  {
    name: 'Brevo',
    slug: 'brevo',
    tagline: 'All-in-One Email Marketing & CRM Platform',
    desc: 'Brevo (formerly Sendinblue) is an all-in-one marketing platform combining email marketing, SMS campaigns, CRM, live chat, and marketing automation. Trusted by over 500,000 businesses worldwide.',
    tags: ['Email Marketing', 'CRM', 'Automation', 'SMS'],
    rating: 4.6,
    reviews: 4120,
    price: 'Free · From $25/mo',
    priceType: 'free',
    link: 'https://www.brevo.com/',
    logoIcon: 'ri-mail-send-line',
    logoGrad: 'from-emerald-500 to-teal-600',
    logoUrl: 'https://storage.readdy-site.link/project_files/3785ac0c-31ac-4f9e-b2ab-27ad3777db2a/4a27a250-b9ed-469c-bf70-ac7a34c6d9e6_brevo-logo.webp?v=9ad1d254cb4c5258b1f3a0ef1eb73968',
    badge: 'Popular',
    badgeColor: 'bg-emerald-100 text-emerald-700',
  },
  {
    name: 'Flux AI',
    slug: 'flux-ai',
    tagline: 'Next-Gen AI Image Generation',
    desc: 'Flux AI is a state-of-the-art AI image generation model by Black Forest Labs, setting new benchmarks in photorealism, prompt adherence, and creative flexibility with exceptional detail and accurate text rendering.',
    tags: ['Image Generation', 'AI Art', 'Photorealistic', 'Design'],
    rating: 4.8,
    reviews: 2150,
    price: 'Free · Pay-per-use',
    priceType: 'free',
    link: 'https://flux-ai.io/?via=hao-nguyen',
    logoIcon: 'ri-image-2-line',
    logoGrad: 'from-pink-500 to-rose-600',
    logoUrl: 'https://storage.readdy-site.link/project_files/3785ac0c-31ac-4f9e-b2ab-27ad3777db2a/71f0f870-a819-450a-96d8-6b11fc0b966f_Flux-AI.webp?v=f6515cb0771eb2ec8bc76b4a7236aec3',
    badge: 'New',
    badgeColor: 'bg-pink-100 text-pink-700',
  },
  {
    name: 'TopView AI',
    slug: 'topview',
    tagline: 'Turn Any URL Into a Viral Video Ad',
    desc: 'TopView AI transforms product pages and website URLs into engaging video advertisements within seconds. Perfect for e-commerce brands and digital marketers looking to scale video ad production.',
    tags: ['Video Ads', 'E-commerce', 'AI Marketing', 'UGC'],
    rating: 4.5,
    reviews: 1340,
    price: 'Free trial · From $29/mo',
    priceType: 'free',
    link: 'https://www.topview.ai/?via=an',
    logoIcon: 'ri-advertisement-line',
    logoGrad: 'from-amber-500 to-orange-500',
    logoUrl: 'https://storage.readdy-site.link/project_files/3785ac0c-31ac-4f9e-b2ab-27ad3777db2a/d9613689-b1a4-471c-9a35-1169ff09c991_topview.png?v=0a93b72fedcaa815feca579b6826de47',
    badge: '',
    badgeColor: '',
  },
  {
    name: 'Tripo3D',
    slug: 'tripo3d',
    tagline: 'AI-Powered 3D Model Generation',
    desc: 'Tripo3D generates high-quality 3D models from text descriptions or single images in under 10 seconds. Supporting export formats for Blender, Unity, Unreal Engine, and more.',
    tags: ['3D Modeling', 'Game Dev', 'AI Design', 'Assets'],
    rating: 4.7,
    reviews: 870,
    price: 'Free · From $20/mo',
    priceType: 'free',
    link: 'https://studio.tripo3d.ai?via=2539',
    logoIcon: 'ri-shapes-line',
    logoGrad: 'from-teal-500 to-cyan-600',
    logoUrl: 'https://storage.readdy-site.link/project_files/3785ac0c-31ac-4f9e-b2ab-27ad3777db2a/a394354e-ca89-4e63-8ab6-608c8bc58f99_Tripo3D-logo.webp?v=4a09488b63a8c27e489a420eb6e4dcf6',
    badge: 'Trending',
    badgeColor: 'bg-teal-100 text-teal-700',
  },
  {
    name: 'Zeemo',
    slug: 'zeemo',
    tagline: 'AI Subtitle & Caption Generator',
    desc: 'Zeemo automatically transcribes and adds captions to your videos with over 98% accuracy across 100+ languages. With animated caption styles and one-click export for TikTok, Reels, and YouTube Shorts.',
    tags: ['Subtitles', 'Captions', 'Translation', 'Video'],
    rating: 4.6,
    reviews: 1560,
    price: 'Free · From $9.99/mo',
    priceType: 'free',
    link: 'https://zeemo.ai/?via=an-luu',
    logoIcon: 'ri-closed-captioning-line',
    logoGrad: 'from-violet-500 to-indigo-600',
    logoUrl: 'https://storage.readdy-site.link/project_files/3785ac0c-31ac-4f9e-b2ab-27ad3777db2a/5d2ce040-4640-4bce-865d-255966c85792_zwmo.webp?v=af0922baa52acb053dc2184236753bcc',
    badge: '',
    badgeColor: '',
  },
  {
    name: 'Soundverse',
    slug: 'soundverse',
    tagline: 'AI Music & Audio Creation Studio',
    desc: 'Soundverse enables anyone to compose, remix, and produce original royalty-free music tracks from text prompts or vocal inputs across any genre in seconds.',
    tags: ['Music AI', 'Audio Creation', 'Royalty-Free', 'Sound Design'],
    rating: 4.4,
    reviews: 720,
    price: 'Free · From $12/mo',
    priceType: 'free',
    link: 'https://www.soundverse.ai/?ref=luu',
    logoIcon: 'ri-music-2-line',
    logoGrad: 'from-fuchsia-500 to-purple-600',
    logoUrl: 'https://storage.readdy-site.link/project_files/3785ac0c-31ac-4f9e-b2ab-27ad3777db2a/d829009a-5ccf-465a-8776-39afc9a67602_soundverse.jpg?v=5d9fb433526c2e90b92d341606e9ec4f',
    badge: '',
    badgeColor: '',
  },
  {
    name: 'Klap',
    slug: 'klap',
    tagline: 'Repurpose Long Videos Into Viral Clips',
    desc: 'Klap uses advanced AI to identify the most engaging moments in long-form videos and transforms them into short-form clips for TikTok, Reels, and YouTube Shorts with auto-reframing and dynamic captions.',
    tags: ['Video Repurposing', 'TikTok', 'Shorts', 'Content Creation'],
    rating: 4.7,
    reviews: 2030,
    price: 'Free trial · From $29/mo',
    priceType: 'free',
    link: 'https://klap.app/?via=an-luu',
    logoIcon: 'ri-scissors-line',
    logoGrad: 'from-red-500 to-orange-500',
    logoUrl: 'https://storage.readdy-site.link/project_files/3785ac0c-31ac-4f9e-b2ab-27ad3777db2a/12796e8b-f610-4bfc-9c21-1f5c3f236adf_klap.webp?v=f0df65cb56d0b71e5fbb4eb0ce4f4af5',
    badge: 'Hot',
    badgeColor: 'bg-red-100 text-red-700',
  },
  {
    name: 'Funfun.art',
    slug: 'funfun-art',
    tagline: 'Creative AI Art Generator',
    desc: 'Funfun.art is an imaginative AI art generation platform with diverse artistic styles from anime and cartoon to oil painting and hyperrealism, making creative expression fun and easy for everyone.',
    tags: ['AI Art', 'Illustration', 'Character Design', 'Creative'],
    rating: 4.3,
    reviews: 640,
    price: 'Free · Credit-based',
    priceType: 'free',
    link: 'https://www.funfun.art/?via=an-luu',
    logoIcon: 'ri-brush-line',
    logoGrad: 'from-pink-400 to-fuchsia-500',
    logoUrl: 'https://storage.readdy-site.link/project_files/3785ac0c-31ac-4f9e-b2ab-27ad3777db2a/c2299868-f224-4e97-bf69-8e11d12667a4_funfun-art.jpg?v=d20e876347ff83878587d179b53df05f',
    badge: '',
    badgeColor: '',
  },
  {
    name: 'Akool',
    slug: 'akool',
    tagline: 'AI Personalized Video & Marketing Platform',
    desc: 'Akool is a cutting-edge AI platform for creating personalized video content, face swapping, avatar generation, and dynamic marketing materials at scale with multilingual dubbing in 40+ languages.',
    tags: ['Personalized Video', 'Face Swap', 'Avatar', 'Marketing'],
    rating: 4.5,
    reviews: 1190,
    price: 'From $30/mo',
    priceType: 'paid',
    link: 'https://akool.com/?via=89f5e8',
    logoIcon: 'ri-vidicon-2-line',
    logoGrad: 'from-teal-400 to-cyan-500',
    logoUrl: 'https://storage.readdy-site.link/project_files/3785ac0c-31ac-4f9e-b2ab-27ad3777db2a/7b1595de-2a58-4106-afff-aaf71c992320_akool.png?v=31876fb09644960fe238b2a3d93b208b',
    badge: '',
    badgeColor: '',
  },
  {
    name: 'TopMediai',
    slug: 'topmediai',
    tagline: 'AI Voice, Video & Media Toolkit',
    desc: 'TopMediai is a comprehensive AI media toolkit offering 200+ tools including text-to-speech with celebrity voices, AI song covers, voice changers, video translation, and face swap.',
    tags: ['Voice Changer', 'AI Covers', 'Video Tools', 'TTS'],
    rating: 4.4,
    reviews: 2800,
    price: 'Free · From $9.99/mo',
    priceType: 'free',
    link: 'https://www.topmediai.com/pricing?from_ua=PCBNYGTM',
    logoIcon: 'ri-equalizer-line',
    logoGrad: 'from-amber-400 to-rose-500',
    logoUrl: 'https://storage.readdy-site.link/project_files/3785ac0c-31ac-4f9e-b2ab-27ad3777db2a/68d669fa-6159-4f93-9706-a5367fe3aeaa_topnedia.jpg?v=7178accfcf6d1421fef690f4cfd4b61b',
    badge: '',
    badgeColor: '',
  },
  {
    name: 'ElevenLabs',
    slug: 'elevenlabs',
    tagline: 'Hyper-Realistic AI Voice Synthesis',
    desc: 'ElevenLabs is the world\'s leading AI voice synthesis platform delivering hyper-realistic speech in 29 languages. Featuring voice cloning, text-to-speech, and an enterprise API trusted by top media and game studios worldwide.',
    tags: ['Voice AI', 'Text to Speech', 'Voice Cloning', 'API'],
    rating: 4.9,
    reviews: 3210,
    price: 'Free – $330/mo',
    priceType: 'freemium',
    link: 'https://try.elevenlabs.io/bthihrkbth7m',
    logoIcon: 'ri-mic-line',
    logoGrad: 'from-violet-500 to-purple-600',
    logoUrl: 'https://storage.readdy-site.link/project_files/3785ac0c-31ac-4f9e-b2ab-27ad3777db2a/e0118c77-998b-448d-887f-db50bbf76d7d_elevenlabs.webp?v=34bfdc19e4e76d7de836c52011a0e6ac',
    badge: 'Top Rated',
    badgeColor: 'bg-violet-100 text-violet-700',
  },
  {
    name: 'Railway',
    slug: 'railway',
    tagline: 'Modern Cloud Deployment Platform',
    desc: 'Railway is a modern infrastructure platform with instant deployments from GitHub, built-in databases, automatic HTTPS, and a clean developer-friendly dashboard removing DevOps complexity.',
    tags: ['Cloud Hosting', 'Deployment', 'DevOps', 'Infrastructure'],
    rating: 4.8,
    reviews: 3420,
    price: 'Free · From $5/mo',
    priceType: 'free',
    link: 'https://railway.com?referralCode=ljCHH7',
    logoIcon: 'ri-cloud-line',
    logoGrad: 'from-slate-600 to-gray-700',
    logoUrl: 'https://storage.readdy-site.link/project_files/3785ac0c-31ac-4f9e-b2ab-27ad3777db2a/f079780b-ca3a-4991-9312-7329836bb17d_logo-ra.png?v=48dba04d46fbf6bbfcebd2a67471dfb9',
    badge: 'Dev Fav',
    badgeColor: 'bg-sky-100 text-sky-700',
  },
];

const relatedCategories = [
  {
    icon: 'ri-line-chart-line',
    grad: 'from-emerald-500 to-teal-600',
    name: 'Finance – Investment',
    desc: 'Financial planning, investment tracking, and wealth management tools',
    count: 38,
    href: '/categories',
  },
  {
    icon: 'ri-store-2-line',
    grad: 'from-orange-500 to-amber-500',
    name: 'Ecommerce & Shopping',
    desc: 'Online stores, marketplaces, and shopping platforms for digital sellers',
    count: 134,
    href: '/categories',
  },
  {
    icon: 'ri-global-line',
    grad: 'from-violet-500 to-fuchsia-600',
    name: 'Marketing & SEO',
    desc: 'Digital marketing, SEO tools, analytics, and growth hacking platforms',
    count: 76,
    href: '/categories',
  },
];

type FilterTab = 'all' | 'free' | 'popular';
type SortOption = 'highest_rating' | 'most_reviews' | 'default';

export default function SoftwareSaasAiPage() {
  const { i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const [signUpEmail, setSignUpEmail] = useState('');
  const [signUpPassword, setSignUpPassword] = useState('');
  const [signUpDone, setSignUpDone] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [filterTab, setFilterTab] = useState<FilterTab>('all');
  const [sortBy, setSortBy] = useState<SortOption>('highest_rating');
  const [sortOpen, setSortOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const sortRef = useRef<HTMLDivElement>(null);

  const currentLang = i18n.language === 'vi' ? 'VI' : 'EN';

  const sortLabels: Record<SortOption, string> = {
    highest_rating: 'Highest rating',
    most_reviews: 'Most reviews',
    default: 'Default',
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false);
      if (sortRef.current && !sortRef.current.contains(e.target as Node)) setSortOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filtered = tools
    .filter((t) => {
      if (filterTab === 'free') return t.priceType === 'free';
      if (filterTab === 'popular') return t.reviews >= 1500;
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'highest_rating') return b.rating - a.rating;
      if (sortBy === 'most_reviews') return b.reviews - a.reviews;
      return 0;
    });

  const renderStars = (rating: number) => {
    const full = Math.floor(rating);
    const half = rating - full >= 0.5;
    return Array.from({ length: 5 }).map((_, i) => {
      if (i < full) return <i key={i} className="ri-star-fill text-amber-400 text-xs"></i>;
      if (i === full && half) return <i key={i} className="ri-star-half-line text-amber-400 text-xs"></i>;
      return <i key={i} className="ri-star-line text-gray-300 text-xs"></i>;
    });
  };

  const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://example.com';

  const pageSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'CollectionPage',
        name: 'Software – SaaS – AI Tools | 247digitrend',
        url: `${siteUrl}/categories/software-saas-ai`,
        description:
          'Discover and compare the best AI-powered platforms, SaaS tools and cloud software. 16 featured tools with expert reviews, ratings, and affiliate links.',
        isPartOf: { '@id': `${siteUrl}/#website` },
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
            { '@type': 'ListItem', position: 2, name: 'Categories', item: `${siteUrl}/categories` },
            { '@type': 'ListItem', position: 3, name: 'Software – SaaS – AI', item: `${siteUrl}/categories/software-saas-ai` },
          ],
        },
      },
      {
        '@type': 'ItemList',
        name: 'Best Software, SaaS & AI Tools 2026',
        url: `${siteUrl}/categories/software-saas-ai`,
        numberOfItems: tools.length,
        itemListElement: tools.map((t, idx) => ({
          '@type': 'ListItem',
          position: idx + 1,
          name: t.name,
          url: `${siteUrl}/categories/software-saas-ai/${t.slug}`,
          item: {
            '@type': 'SoftwareApplication',
            name: t.name,
            description: t.tagline,
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: t.rating,
              reviewCount: t.reviews,
              bestRating: 5,
            },
          },
        })),
      },
    ],
  };

  return (
    <div className="min-h-screen" style={{ background: '#f7f8fa', fontFamily: "'Inter', sans-serif" }}>
      <PageMeta
        title="Best Software, SaaS & AI Tools (2026) – Compare & Reviews | 247digitrend"
        description="Compare the best AI tools and SaaS platforms in 2026. Expert reviews of InVideo, HeadshotPro, Magai, Brevo, Flux AI, Railway and 10+ more. Ratings, pricing, and honest analysis."
        canonical={`${siteUrl}/categories/software-saas-ai`}
        keywords="best AI tools 2026, SaaS comparison, AI software review, InVideo review, HeadshotPro review, top AI platforms"
      />
      <JsonLd data={pageSchema} />
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap" rel="stylesheet" />

      {/* ── SIGN UP MODAL ── */}
      {showSignUp && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => { setShowSignUp(false); setSignUpDone(false); }}>
          <div className="relative bg-white rounded-2xl w-full max-w-sm mx-4 p-8" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => { setShowSignUp(false); setSignUpDone(false); }} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer">
              <i className="ri-close-line text-gray-600 text-base"></i>
            </button>
            {signUpDone ? (
              <div className="text-center py-4">
                <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 rounded-full bg-emerald-100">
                  <i className="ri-check-line text-emerald-600 text-2xl"></i>
                </div>
                <h3 className="text-lg font-black text-gray-900 mb-2">Registration Successful!</h3>
                <p className="text-sm text-gray-500">Welcome to 247digitrend.</p>
                <button onClick={() => { setShowSignUp(false); setSignUpDone(false); }} className="mt-6 w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold py-3 rounded-full cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap">Close</button>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-xl bg-gradient-to-br from-violet-500 via-fuchsia-500 to-pink-500">
                    <i className="ri-bar-chart-grouped-line text-white text-base"></i>
                  </div>
                  <h3 className="text-xl font-black text-gray-900 mb-1">Create Account</h3>
                  <p className="text-xs text-gray-500">Join 50K+ users of 247digitrend</p>
                </div>
                <form onSubmit={(e) => { e.preventDefault(); setSignUpDone(true); }} className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Email</label>
                    <input type="email" required value={signUpEmail} onChange={(e) => setSignUpEmail(e.target.value)} placeholder="your@email.com" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl outline-none focus:border-violet-400 transition-colors" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Password</label>
                    <input type="password" required value={signUpPassword} onChange={(e) => setSignUpPassword(e.target.value)} placeholder="At least 8 characters" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl outline-none focus:border-violet-400 transition-colors" />
                  </div>
                  <button type="submit" className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold py-3 rounded-full cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap mt-2">Sign Up Now</button>
                </form>
                <p className="text-xs text-center text-gray-400 mt-4">By signing up, you agree to our <a href="#" className="text-violet-600 underline">Terms</a>.</p>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── HEADER ── */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-sm border-b border-gray-100' : 'bg-white border-b border-gray-100'}`}>
        <div className="mx-auto px-8 py-2 flex items-center justify-between">
          <a href="/" className="flex items-center cursor-pointer">
            <img src="https://storage.readdy-site.link/project_files/3785ac0c-31ac-4f9e-b2ab-27ad3777db2a/7544398a-500f-41a6-81c3-aa7efbf1c0d2_logo-247.png?v=04135332188f9bc8b00780c66cd5837d" alt="247digitrend" className="h-36 w-auto object-contain" />
          </a>
          <nav className="hidden md:flex items-center gap-8">
            <a href="/" className="text-[15px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Home</a>
            <a href="/categories" className="text-[15px] font-semibold text-violet-600 transition-colors cursor-pointer">Categories</a>
            <a href="/reviews" className="text-[15px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Reviews</a>
            <a href="/comparisons" className="text-[15px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Comparisons</a>
            <a href="/blog" className="text-[15px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Blog</a>
            <button className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer">
              <i className="ri-search-line text-gray-500 text-sm"></i>
            </button>
          </nav>
          <div className="flex items-center gap-3">
            <div className="relative" ref={langRef}>
              <button onClick={() => setLangOpen(!langOpen)} className="flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 transition-colors rounded-full px-3 py-1.5 cursor-pointer">
                <i className="ri-translate-2 text-xs text-gray-500"></i>
                <span className="text-xs font-semibold text-gray-700">{currentLang}</span>
                <i className={`ri-arrow-down-s-line text-xs text-gray-400 transition-transform ${langOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {langOpen && (
                <div className="absolute right-0 top-full mt-1.5 bg-white rounded-xl border border-gray-100 overflow-hidden w-32" style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
                  {(['VI', 'EN'] as const).map((lang) => (
                    <button key={lang} onClick={() => { i18n.changeLanguage(lang.toLowerCase()); setLangOpen(false); }} className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold transition-colors cursor-pointer ${currentLang === lang ? 'bg-violet-50 text-violet-700' : 'text-gray-600 hover:bg-gray-50'}`}>
                      <span>{lang === 'VI' ? '🇻🇳' : '🇺🇸'}</span>
                      <span>{lang === 'VI' ? 'Tiếng Việt' : 'English'}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button onClick={() => setShowSignUp(true)} className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold px-5 py-2 rounded-full whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity">
              Sign Up
            </button>
          </div>
        </div>
      </header>

      {/* ── PAGE CONTENT ── */}
      <main className="pt-28 pb-16">
        <div className="max-w-5xl mx-auto px-6">

          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 mb-6">
            <a href="/" className="w-7 h-7 flex items-center justify-center rounded-lg bg-white border border-gray-200 hover:border-violet-300 transition-colors cursor-pointer">
              <i className="ri-home-4-line text-gray-500 text-xs"></i>
            </a>
            <i className="ri-arrow-right-s-line text-gray-400 text-sm"></i>
            <a href="/categories" className="w-7 h-7 flex items-center justify-center rounded-lg bg-white border border-gray-200 hover:border-violet-300 transition-colors cursor-pointer">
              <i className="ri-apps-2-line text-gray-500 text-xs"></i>
            </a>
          </nav>

          {/* Category Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-black text-gray-900 mb-2 tracking-tight">Software – SaaS – AI</h1>
            <p className="text-sm text-gray-500 mb-4">Cloud-based software solutions, AI-powered platforms and next-generation digital tools</p>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-full px-3 py-1.5">
                <i className="ri-tools-line text-gray-500 text-xs"></i>
                <span className="text-xs font-semibold text-gray-700">{tools.length} tools</span>
              </div>
              <div className="flex items-center gap-1.5 bg-amber-50 border border-amber-200 rounded-full px-3 py-1.5">
                <i className="ri-star-fill text-amber-500 text-xs"></i>
                <span className="text-xs font-semibold text-amber-700">Highly rated</span>
              </div>
            </div>
          </div>

          {/* Filter + Sort Bar */}
          <div className="flex items-center justify-between mb-6">
            {/* Filter tabs */}
            <div className="flex items-center bg-white border border-gray-200 rounded-full px-1 py-1 gap-0.5">
              {(['all', 'free', 'popular'] as FilterTab[]).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setFilterTab(tab)}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap capitalize ${
                    filterTab === tab
                      ? 'bg-violet-600 text-white'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>

            {/* Sort dropdown */}
            <div className="relative" ref={sortRef}>
              <button
                onClick={() => setSortOpen(!sortOpen)}
                className="flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-2 text-xs font-semibold text-gray-700 hover:border-gray-300 transition-colors cursor-pointer whitespace-nowrap"
              >
                <span>Sort by: {sortLabels[sortBy]}</span>
                <i className={`ri-arrow-down-s-line text-gray-400 transition-transform ${sortOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {sortOpen && (
                <div className="absolute right-0 top-full mt-1.5 bg-white rounded-xl border border-gray-100 overflow-hidden w-44 z-10" style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}>
                  {(Object.entries(sortLabels) as [SortOption, string][]).map(([key, label]) => (
                    <button key={key} onClick={() => { setSortBy(key); setSortOpen(false); }} className={`w-full flex items-center gap-2 px-4 py-2.5 text-xs font-semibold transition-colors cursor-pointer text-left ${sortBy === key ? 'bg-violet-50 text-violet-700' : 'text-gray-600 hover:bg-gray-50'}`}>
                      {sortBy === key && <i className="ri-check-line text-violet-600"></i>}
                      <span className={sortBy === key ? '' : 'ml-4'}>{label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Tools List */}
          <div className="space-y-3">
            {filtered.map((tool, i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-200 p-5 hover:border-violet-200 transition-all group">
                <div className="flex items-start gap-4">
                  {/* Logo */}
                  {tool.logoUrl ? (
                    <div className="w-12 h-12 flex-shrink-0 rounded-xl overflow-hidden bg-white border border-gray-100 flex items-center justify-center p-1">
                      <img src={tool.logoUrl} alt={tool.name} className="w-full h-full object-contain" />
                    </div>
                  ) : (
                    <div className={`w-12 h-12 flex items-center justify-center flex-shrink-0 rounded-xl bg-gradient-to-br ${tool.logoGrad}`}>
                      <i className={`${tool.logoIcon} text-white text-xl`}></i>
                    </div>
                  )}

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        {/* Name + badge */}
                        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                          <h3 className="text-base font-bold text-gray-900">{tool.name}</h3>
                          {tool.badge && (
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${tool.badgeColor}`}>{tool.badge}</span>
                          )}
                        </div>
                        {/* Tagline */}
                        <p className="text-xs font-medium text-violet-600 mb-1.5">{tool.tagline}</p>
                        {/* Description */}
                        <p className="text-xs text-gray-500 leading-relaxed line-clamp-2 mb-3">{tool.desc}</p>
                        {/* Tags */}
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {tool.tags.map((tag, ti) => (
                            <span key={ti} className="text-xs font-medium text-gray-500 bg-gray-100 rounded-full px-2.5 py-0.5">{tag}</span>
                          ))}
                        </div>
                        {/* Price */}
                        <p className="text-xs text-gray-500">
                          <i className="ri-price-tag-3-line mr-1 text-gray-400"></i>
                          {tool.price}
                        </p>
                      </div>

                      {/* Right: Rating + Buttons */}
                      <div className="flex flex-col items-end gap-3 flex-shrink-0">
                        {/* Rating */}
                        <div className="text-right">
                          <div className="flex items-center gap-1 justify-end mb-0.5">
                            <div className="flex items-center gap-0.5">
                              {renderStars(tool.rating)}
                            </div>
                            <span className="text-sm font-bold text-gray-800 ml-0.5">{tool.rating}</span>
                          </div>
                          <span className="text-xs text-gray-400">({tool.reviews.toLocaleString()} reviews)</span>
                        </div>

                        {/* Buttons */}
                        <div className="flex items-center gap-2">
                          <a
                            href={`/categories/software-saas-ai/${tool.slug}`}
                            className="flex items-center gap-1.5 px-3.5 py-2 border border-gray-300 text-gray-600 hover:border-violet-400 hover:text-violet-600 text-xs font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap"
                          >
                            View Details
                          </a>
                          <a
                            href={tool.link}
                            target="_blank"
                            rel="noopener noreferrer nofollow"
                            className="flex items-center gap-1.5 px-3.5 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-external-link-line text-xs"></i>
                            Access
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {filtered.length === 0 && (
              <div className="text-center py-16 bg-white rounded-2xl border border-gray-200">
                <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 rounded-full bg-gray-100">
                  <i className="ri-inbox-line text-2xl text-gray-400"></i>
                </div>
                <p className="text-sm font-semibold text-gray-500">No tools found</p>
                <p className="text-xs text-gray-400 mt-1">Try a different filter</p>
              </div>
            )}
          </div>

          {/* ── RELATED CATEGORIES ── */}
          <div className="mt-12">
            <h2 className="text-lg font-bold text-gray-900 mb-5">Related categories</h2>
            <div className="grid grid-cols-3 gap-4">
              {relatedCategories.map((cat, i) => (
                <a key={i} href={cat.href} className="bg-white rounded-2xl border border-gray-200 p-5 hover:border-violet-200 transition-all cursor-pointer group">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br ${cat.grad} mb-3`}>
                    <i className={`${cat.icon} text-white text-lg`}></i>
                  </div>
                  <h3 className="text-sm font-bold text-gray-900 mb-1 group-hover:text-violet-600 transition-colors">{cat.name}</h3>
                  <p className="text-xs text-gray-500 leading-relaxed mb-3">{cat.desc}</p>
                  <div className="flex items-center gap-1 text-xs text-gray-400">
                    <i className="ri-tools-line"></i>
                    <span>{cat.count} tools</span>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </main>

      {/* ── FOOTER ── */}
      <SiteFooter />

      <FloatingCtaBar onSignUp={() => setShowSignUp(true)} />
    </div>
  );
}
